import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function StokeOnTrentCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Stoke-on-Trent Pothole Claims | 87% of Roads Without SCANNER Data | Fixtyer"
        description="Stoke-on-Trent City Council maintains 864km of unclassified roads surveyed by visual inspection only. DfT rates the city AMBER on incomplete condition data. See what this means for your pothole claim."
        canonical="/councils/stoke-on-trent"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Stoke-on-Trent', url: '/councils/stoke-on-trent' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Is Stoke-on-Trent the same authority as Staffordshire County Council?',
            answer: 'No. Stoke-on-Trent City Council is a unitary authority responsible for most roads within the city boundary. Staffordshire County Council maintains highways outside the city. If your pothole was inside Stoke-on-Trent, your claim goes to the city council — not Staffordshire.',
          },
          {
            question: 'The DfT says Stoke\'s condition rating is based on incomplete data — does that help my claim?',
            answer: 'It can. The Department for Transport flags that Stoke-on-Trent\'s 2025/26 condition scorecard reflects incomplete road condition data. The council\'s own report publishes SCANNER survey results for just 128km of classified roads, while 864.63km of unclassified roads — 87% of the network — are assessed by visual inspection only, with no published red/amber/green percentages.',
          },
          {
            question: 'Stoke-on-Trent has a GREEN spend rating — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN because projected 2025/26 capital spend (£9.679m) is more than double the DfT allocation (£4.697m). Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate spending. The overall and condition ratings are both AMBER.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'Unclassified roads make up 864.63km — 87% of Stoke-on-Trent\'s 993km network. The council conducts "regular visual inspections" on U-roads but publishes no SCANNER-style condition breakdown for them. If your pothole was on a U-road, the council\'s published condition evidence simply does not cover your street.',
          },
          {
            question: 'The council plans 13,000 defect repairs in 2025/26 — does that mean the roads are fixed?',
            answer: 'No. The council estimates it will repair around 13,000 defects in 2025/26 while also projecting 3,609 pothole fills. That volume of reactive work indicates defects are forming continuously across the network — it is evidence of ongoing deterioration, not proof that your specific road was maintained to a reasonable standard.',
          },
          {
            question: 'Classified road condition has improved — does that weaken my claim?',
            answer: 'Only if your pothole was on an A, B or C road. SCANNER data shows A-road RED condition fell from 6% to 2% between 2020 and 2024, and B/C roads improved similarly — but classified roads are just 128km of a 993km network. Most Stoke-on-Trent pothole damage happens on unclassified residential and estate roads where no comparable condition data is published.',
          },
          {
            question: 'How do I report a pothole to Stoke-on-Trent City Council?',
            answer: 'Report highways incidents via the council\'s online form at stoke.gov.uk. Prior reports of the same defect strengthen a claim by demonstrating the council had notice before your incident. Fixtyer searches for existing reports and attaches them to your claim pack.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Stoke-on-Trent City Council"
        councilSlug="stoke-on-trent"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/stoke-on-trent"
        headline="Stoke-on-Trent Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Stoke-on-Trent</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 mb-6 max-w-3xl">
            <p className="text-amber-200 text-sm">
              <strong className="text-amber-400">DfT caveat:</strong> The Department for Transport flags that
              Stoke-on-Trent's 2025/26 condition ratings are based on{' '}
              <strong className="text-white">incomplete road condition data</strong>.
            </p>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            864km of Stoke roads have no SCANNER survey data
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Stoke-on-Trent City Council — a unitary authority, distinct from Staffordshire County Council — maintains
            993km of roads. Just 128km carry published SCANNER condition scores. The other{' '}
            <strong className="text-rose-400">864.63km of unclassified roads (87% of the network)</strong> are assessed
            by visual inspection only. The DfT rates the city <strong className="text-amber-400">AMBER</strong> overall
            on incomplete data, despite projected capital spend of{' '}
            <strong className="text-emerald-400">£9.679m — more than double the £4.697m DfT allocation</strong>.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">87%</div>
            <div className="text-white font-medium mt-2">Of the network without SCANNER data</div>
            <div className="text-brand-300 text-sm mt-1">
              864.63km of unclassified roads across the Potteries — surveyed by eye, not laser. No published
              red/amber/green percentages for the roads where most pothole damage occurs.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the published condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER survey data from Stoke-on-Trent City Council's transparency report — covering
            only A, B and C roads (128km of 993km)
          </p>

          {/* Network breakdown */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">Stoke-on-Trent highway network (June 2025)</h3>
            <div className="grid sm:grid-cols-4 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-900">97.7km</div>
                <div className="text-sm text-surface-600">A roads</div>
                <div className="text-xs text-surface-500">9.8% of network</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-900">30.7km</div>
                <div className="text-sm text-surface-600">B and C roads</div>
                <div className="text-xs text-surface-500">3.1% of network</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">864.6km</div>
                <div className="text-sm text-rose-800">U roads</div>
                <div className="text-xs text-surface-500">87.1% of network</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-900">993km</div>
                <div className="text-sm text-surface-600">Total roads</div>
                <div className="text-xs text-surface-500">plus 1,127km footways</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Some major routes (motorways and some A roads) are maintained by National Highways, not the city council.
              Private and unadopted streets may also fall outside council control.
            </p>
          </div>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (97.7km): improving on paper</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">29%</td>
                    <td className="px-4 py-2 text-center">65%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">7%</td>
                    <td className="px-4 py-2 text-center">36%</td>
                    <td className="px-4 py-2 text-center">57%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">22%</td>
                    <td className="px-4 py-2 text-center">75%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">21%</td>
                    <td className="px-4 py-2 text-center">76%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">2%</td>
                    <td className="px-4 py-2 text-center font-bold">22%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">76%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road RED condition has fallen from 6% to 2% over five years. A-roads are less than 10% of the
              network — and some are maintained by National Highways rather than the city council.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (30.67km — 3.1% of network): improved, but tiny</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">9%</td>
                    <td className="px-4 py-2 text-center">50%</td>
                    <td className="px-4 py-2 text-center">41%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">9%</td>
                    <td className="px-4 py-2 text-center">31%</td>
                    <td className="px-4 py-2 text-center">60%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">24%</td>
                    <td className="px-4 py-2 text-center">70%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">21%</td>
                    <td className="px-4 py-2 text-center">76%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">3.5%</td>
                    <td className="px-4 py-2 text-center font-bold">25%</td>
                    <td className="px-4 py-2 text-center font-bold">71.5%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              B/C roads have improved — RED condition fell from 9% to 3.5%. But this entire road class is just
              30.67km. In 2020, half of that small network was AMBER ("maintenance may be required soon"). The
              improvement covers a fraction of the city.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">GREEN spend, AMBER condition</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£4.697m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£9.679m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">36.0%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Stoke-on-Trent projects spending more than{' '}
              <span className="text-emerald-400">double its DfT allocation</span> — earning a GREEN spend
              scorecard. Yet the overall rating stays <span className="text-amber-400">AMBER</span>, and the DfT
              notes the condition data behind that rating is incomplete.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road blind spot */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 864km blind spot
          </h2>
          <p className="text-surface-600 mb-8">
            87% of Stoke-on-Trent's network is unclassified roads — with no published SCANNER condition breakdown
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Visual inspection, not laser survey</h3>
              <p className="text-surface-600 text-sm mb-3">
                The council's transparency report states it uses SCANNER surveys annually for classified A, B and
                C roads. For unclassified roads, which "make up a large portion of our network," it conducts
                "regular visual inspections to assess their state."
              </p>
              <p className="text-surface-600 text-sm">
                No red, amber or green percentages are published for U-roads. The DfT's incomplete-data flag
                reflects this gap: the condition scorecard cannot fully represent roads that are never laser-surveyed.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">PAS 2161 from 2026/27 — not yet</h3>
              <p className="text-amber-800 text-sm mb-3">
                The council notes that from 2026/27 the government will introduce PAS 2161, a new national standard
                for road condition monitoring with a five-category classification. Stoke-on-Trent is "preparing for
                this change by investing in Artificial Intelligence (AI) inspections."
              </p>
              <p className="text-amber-800 text-sm">
                AI inspections are a future plan. Today, 864.63km of residential and estate roads rely on visual
                checks — with no published condition history for claimants to assess.
              </p>
            </div>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Over recent years, we have focused on preventative maintenance to slow deterioration and extend the life of our roads. However, like many areas, increasing traffic, extreme weather, and funding pressures have impacted road condition."
              source="Stoke-on-Trent City Council Local Highways Maintenance Transparency Report (June 2025)"
            />
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why this matters for Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing the
              condition of its roads. For Stoke-on-Trent's unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road ever SCANNER-surveyed — or only visually inspected?</li>
              <li>• What condition record exists for your street when 87% of the network has no published RAG data?</li>
              <li>• If the council admits weather and funding pressures have impacted condition, what was done on your road?</li>
              <li>• Can the council prove reasonable knowledge of a defect on a road type it does not laser-survey?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim detailed network knowledge when its own published data covers just 13% of
              road length — and the DfT agrees the picture is incomplete.
            </p>
          </div>
        </div>
      </section>

      {/* Spending and repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            12,836 potholes filled in four years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volumes from the council's own transparency report — an average of 3,209 potholes per year
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Preventative spend</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Reactive spend</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">Not separately reported</td>
                  <td className="px-6 py-4 text-center">24.87%</td>
                  <td className="px-6 py-4 text-center">12%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">2,989</td>
                  <td className="px-6 py-4 text-center">21.16%</td>
                  <td className="px-6 py-4 text-center">13%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">3,236</td>
                  <td className="px-6 py-4 text-center">26.24%</td>
                  <td className="px-6 py-4 text-center">16%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">3,062</td>
                  <td className="px-6 py-4 text-center">26.65%</td>
                  <td className="px-6 py-4 text-center">17%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">3,549</td>
                  <td className="px-6 py-4 text-center">34.62%</td>
                  <td className="px-6 py-4 text-center">21%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2025/26 (projected)</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">3,609</td>
                  <td className="px-6 py-4 text-center">35.99%</td>
                  <td className="px-6 py-4 text-center">19%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Four-year total (2021/22–2024/25)</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">12,836</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic" colSpan={2}>
                    Council average: 3,209 potholes/year
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">13,000 defects planned for 2025/26</h3>
              <p className="text-surface-600 text-sm">
                The council's 2025/26 plan estimates around 13,000 defect repairs across the network — a broader
                figure than pothole fills alone. Pothole repairs account for around 18% of the overall reactive
                maintenance budget, according to the council's own report.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Prevention rising, failures continuing</h3>
              <p className="text-amber-800 text-sm">
                Estimated preventative maintenance share has risen from 21.16% (2021/22) to 35.99% (2025/26 projected).
                Yet pothole fills hit a four-year high of 3,549 in 2024/25. Higher preventative spending alongside
                rising reactive repairs suggests defects are still forming faster than the network is stabilised.
              </p>
            </div>
          </div>

          {/* Capital spend table */}
          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden">
            <h3 className="font-semibold text-brand-900 px-6 py-4 border-b border-surface-200">
              Highway maintenance spending (capital and revenue, £000s)
            </h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-2 text-center font-semibold text-brand-900">DfT allocation</th>
                  <th className="px-4 py-2 text-center font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-2 text-center font-semibold text-brand-900">Revenue spend</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-2 font-mono">2020/21</td>
                  <td className="px-4 py-2 text-center">£5,695</td>
                  <td className="px-4 py-2 text-center">£12,155</td>
                  <td className="px-4 py-2 text-center">£1,492</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-2 font-mono">2021/22</td>
                  <td className="px-4 py-2 text-center">£4,651</td>
                  <td className="px-4 py-2 text-center">£14,039</td>
                  <td className="px-4 py-2 text-center">£1,911</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-2 font-mono">2022/23</td>
                  <td className="px-4 py-2 text-center">£4,651</td>
                  <td className="px-4 py-2 text-center">£13,847</td>
                  <td className="px-4 py-2 text-center">£2,193</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-2 font-mono">2023/24</td>
                  <td className="px-4 py-2 text-center">£3,878</td>
                  <td className="px-4 py-2 text-center">£12,255</td>
                  <td className="px-4 py-2 text-center">£2,102</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-2 font-mono">2024/25</td>
                  <td className="px-4 py-2 text-center">£3,350</td>
                  <td className="px-4 py-2 text-center">£9,072</td>
                  <td className="px-4 py-2 text-center">£1,960</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-4 py-2 font-mono font-bold">2025/26 (projected)</td>
                  <td className="px-4 py-2 text-center font-bold">£4,697</td>
                  <td className="px-4 py-2 text-center font-bold text-emerald-600">£9,679</td>
                  <td className="px-4 py-2 text-center font-bold">£1,848</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Innovation section */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Innovation plans vs today's evidence gap
          </h2>
          <p className="text-surface-600 mb-8">
            The council's transparency report describes ambitious technology — but published condition data lags behind
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What the council is investing in</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• AI-powered condition surveys to reduce reliance on traditional surveys</li>
                <li>• Pothole Pro machine — repairs three times faster than traditional methods</li>
                <li>• Warm mix asphalt cutting carbon emissions by up to 15%</li>
                <li>• ~50 miles of carriageway rehabilitation planned for 2025/26</li>
                <li>• Multi-functional machine trialling gully cleansing, pothole repair and road marking in one pass</li>
              </ul>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">What claimants can actually cite today</h3>
              <p className="text-amber-800 text-sm mb-3">
                Innovation programmes describe future capability. For a claim about a pothole that damaged your
                vehicle yesterday, what matters is inspection records, repair logs and condition data for your
                specific road — not plans for AI surveys from 2026/27 onwards.
              </p>
              <p className="text-amber-800 text-sm">
                The council manages 40,000 streetlights, 282 traffic signal sets and 242.8km of public rights of
                way — but publishes SCANNER condition scores for just 128km of classified roads.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="We aim to spend on average 30% of our budget on preventative treatments. However, we also have to react quickly to urgent issues such as potholes and damaged road surfaces. Our teams regularly inspect the network, and we prioritise repairs based on risk and safety concerns."
            source="Stoke-on-Trent City Council Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* What AMBER means for claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against an AMBER-rated city council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Stoke-on-Trent invests heavily — but its condition evidence has a structural gap
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council's favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — projected capital spend more than doubles DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Classified A and B/C road condition has improved on published SCANNER data
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Preventative maintenance share rising — 36% projected for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset management approach and technology investment
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence on classified A-roads where SCANNER data shows improvement.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> DfT flags condition ratings as based on incomplete road condition data
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 864.63km of U-roads (87%) with no published SCANNER condition breakdown
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits weather and funding pressures have impacted road condition
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 12,836 pothole fills in four years — defects forming continuously
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 13,000 defect repairs planned for 2025/26 — reactive workload remains high
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend but incomplete condition data, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect and road type</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the absence of SCANNER data is your strongest structural argument</li>
              <li>• Whether the council can produce inspection records for your street, not just network-wide averages</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and cites
              Stoke-on-Trent's own transparency data — including the DfT's incomplete-data caveat — where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a pothole to Stoke-on-Trent City Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. Do this before claiming — and tell us
            when you reported it so we can reference it in your pack.
          </p>
          <a
            href="https://www.stoke.gov.uk/info/20008/roads_parking_and_travel/522/report_a_highways_incident"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a highways incident — stoke.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Stoke-on-Trent?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            Incomplete condition data demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road data-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No DfT incomplete-data citation</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 864km U-road evidence gap documented</li>
                <li>✅ DfT incomplete-data caveat cited</li>
                <li>✅ 12,836 pothole fills in four years referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Stoke-on-Trent</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=stoke-on-trent"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order my Stoke-on-Trent claim pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Is Stoke-on-Trent the same authority as Staffordshire County Council?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Stoke-on-Trent City Council is a unitary authority responsible for most roads within the city
                boundary. Staffordshire County Council maintains highways outside the city. If your pothole was
                inside Stoke-on-Trent, your claim goes to the city council — not Staffordshire.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                The DfT says Stoke's condition rating is based on incomplete data — does that help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It can. The Department for Transport flags that Stoke-on-Trent's 2025/26 condition scorecard reflects
                incomplete road condition data. The council's own report publishes SCANNER survey results for just
                128km of classified roads, while 864.63km of unclassified roads — 87% of the network — are assessed
                by visual inspection only, with no published red/amber/green percentages.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Stoke-on-Trent has a GREEN spend rating — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN because projected 2025/26 capital spend (£9.679m) is more than
                double the DfT allocation (£4.697m). Section 58 turns on whether the specific defect that damaged
                your vehicle was reasonably inspected and repaired — not on aggregate spending. The overall and
                condition ratings are both AMBER.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 864.63km — 87% of Stoke-on-Trent's 993km network. The council conducts
                "regular visual inspections" on U-roads but publishes no SCANNER-style condition breakdown for them.
                If your pothole was on a U-road, the council's published condition evidence simply does not cover
                your street.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                The council plans 13,000 defect repairs in 2025/26 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. The council estimates it will repair around 13,000 defects in 2025/26 while also projecting
                3,609 pothole fills. That volume of reactive work indicates defects are forming continuously across
                the network — it is evidence of ongoing deterioration, not proof that your specific road was
                maintained to a reasonable standard.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Classified road condition has improved — does that weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Only if your pothole was on an A, B or C road. SCANNER data shows A-road RED condition fell from 6%
                to 2% between 2020 and 2024, and B/C roads improved similarly — but classified roads are just 128km
                of a 993km network. Most Stoke-on-Trent pothole damage happens on unclassified residential and estate
                roads where no comparable condition data is published.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                How do I report a pothole to Stoke-on-Trent City Council?
              </h3>
              <p className="text-surface-600 text-sm">
                Report highways incidents via the council's online form at{' '}
                <a
                  href="https://www.stoke.gov.uk/info/20008/roads_parking_and_travel/522/report_a_highways_incident"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-600 hover:underline"
                >
                  stoke.gov.uk
                </a>
                . Prior reports of the same defect strengthen a claim by demonstrating the council had notice before
                your incident. Fixtyer searches for existing reports and attaches them to your claim pack.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.stoke.gov.uk/highwaysmaintenance"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Stoke-on-Trent City Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
